// Imports ------------------------------------------------------------------->

import {StringEnum, LoyaltyProgramCoupon, Category} from "./CommonDefinitions";
import {Flyer} from "./FlyerDefinitions";
import { normalizeDate } from "../utils/date";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export class CouponCategory extends Category {}

export class CouponType extends StringEnum {
	static AMOUNT_OFF = new CouponType("amountoff");
	static PERCENT_OFF = new CouponType("percentoff");
	static BUYX_GETY = new CouponType("buyxgety");
}

export class CouponRedemptionMethod extends StringEnum {
	static PRINT = new CouponRedemptionMethod("print");
	static MOBILE = new CouponRedemptionMethod("mobile");
	static SAVE_TO_CARD = new CouponRedemptionMethod("savetocard");
	static PICTURE = new CouponRedemptionMethod("picture");
}

export class CouponRedemptionStatus extends StringEnum {
	static CLIPPED = new CouponRedemptionStatus("clipped");
	static REDEEMED = new CouponRedemptionStatus("redeemed");
	static UNAVAILABLE = new CouponRedemptionStatus("unavailable");
}

export class Coupon {
	availableFrom: Date;
	availableTo: Date;
	barcodeImageUrl: string;
	brand: string;
	brandLogoUrl: string;
	categories: string[];
	id: number;
	imageUrl: string;
	type: CouponType;
	vendorId: number;
	dollarsOff: number;
	merchantId: number;
	merchantLogoUrl: string;
	merchantName: string;
	percentOff: number;
	postalCode: string;
	priority: number;
	promotionText: string;
	qualifyingQuantity: number;
	redemptionMethod: CouponRedemptionMethod;
	rewardQuantity: number;
	saleStory: string;
	timezone: number;
	useSalesStory: boolean;
	validFrom: Date;
	validTo: Date;
	matchUps: FlyerItemCoupon[];
	available: boolean;
	rawBrand: string;

	constructor(json: any) {
		const coupon_type =
				(json.coupon_type === "amountoff") ?
							CouponType.AMOUNT_OFF :
				(json.coupon_type === "percentoff") ?
							CouponType.PERCENT_OFF :
				(json.coupon_type === "buyxgety") ?
							CouponType.BUYX_GETY : null;
		const redemption_method =
				(json.redemption_method === "print") ?
							CouponRedemptionMethod.PRINT :
				(json.redemption_method === "mobile") ?
							CouponRedemptionMethod.MOBILE :
				(json.redemption_method === "savetocard") ?
							CouponRedemptionMethod.SAVE_TO_CARD :
				(json.redemption_method === "picture") ?
							CouponRedemptionMethod.PICTURE : null;

		this.availableFrom = normalizeDate(json.available_from);
		this.availableTo = normalizeDate(json.available_to, true);
		this.barcodeImageUrl = json.barcode_image_url;
		this.brand = json.brand;
		this.brandLogoUrl = json.brand_logo_url;
		this.categories = json.categories;
		this.id = json.coupon_id;
		this.imageUrl = json.coupon_image_url;
		this.type = coupon_type;
		this.vendorId = json.coupon_vendor_id;
		this.dollarsOff = json.dollars_off;
		this.merchantId = json.merchant_id;
		this.merchantLogoUrl = json.merchant_logo_url;
		this.merchantName = json.merchant_name;
		this.percentOff = json.percent_off;
		this.postalCode = json.postal_code;
		this.priority = json.priority;
		this.promotionText = json.promotion_text;
		this.qualifyingQuantity = json.qualifying_quantity;
		this.redemptionMethod = redemption_method;
		this.rewardQuantity = json.reward_quantity;
		this.saleStory = json.sale_story;
		this.timezone = json.timezone;
		this.useSalesStory = json.use_sales_story;
		this.validFrom = json.valid_from ? normalizeDate(json.valid_from) : null;
		this.validTo = json.valid_to ? normalizeDate(json.valid_to, true) : null;
		this.rawBrand = json.raw_brand;
		this.available = true;
	}

	getName() {
		return this.saleStory || this.promotionText ||  "";
	}

	getExpired() {
		return this.availableTo ? this.availableTo < new Date() : false;
	}

	getDiscount() {
		if (this.percentOff) {
			return `${(this.percentOff * 100).toFixed(0)}%`;
		}

		if (this.dollarsOff) {
			return `$${this.dollarsOff}`;
		}

		return null;
	}
}

export interface FlyerItemCoupon {
	id: number;
	flyerId: number;
	flyer: Flyer;
	flyerItemId: number;
	merchant: string;
	merchantLogo: string;
	couponId: number;
	left: number;
	bottom: number;
	right: number;
	top: number;
	item: {
		id: number;
		currentPrice: number;
		originalPrice: number;
		dollarsOff: number;
		cutoutUrl: string;
	};
	couponDollarsOff: number;
	totalSavings: number;
	finalPrice: number;
	percentOff: number;
	displayFinalPrice: boolean;
	displayType: number;
	itemRank: number;
}

export interface ClippedCoupon {
	program: LoyaltyProgramCoupon;
	status: CouponRedemptionStatus;
}

export interface ActiveClippedCoupons {
	[key: number]: ClippedCoupon;
}
